/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent, PreloadFetchPolicy } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import Query from './__generated__/UserSegmentationRedirectEntryQuery$parameters';
import type { UserSegmentationRedirectEntryQuery } from './__generated__/UserSegmentationRedirectEntryQuery.graphql';

const fetchPolicy: PreloadFetchPolicy = 'store-or-network';

/**
 * Cookie name - inlined to avoid importing constants file
 */
const USER_SEG_REDIRECT_CHECKED_COOKIE = 'user_seg_redirect_checked';

declare global {
	// eslint-disable-next-line vars-on-top
	var __GET_COOKIE__:
		| ((name?: string) => string | { [key: string]: string } | undefined)
		| undefined;
}

/**
 * Check if redirect check cookie exists in SSR.
 *
 * Note: globalThis.__GET_COOKIE__ is only available in SSR context.
 * Client-side checks are handled in the component itself.
 */
const hasRedirectCheckCookie = (): boolean => {
	// Check if the global cookie reader is available (SSR or test environment)
	if (globalThis?.__GET_COOKIE__) {
		const cookie = globalThis.__GET_COOKIE__(USER_SEG_REDIRECT_CHECKED_COOKIE);
		return Boolean(cookie);
	}

	return false;
};

type UserSegRedirectEntrypointComponent = EntryPointComponent<
	{
		userSegQueryRef?: UserSegmentationRedirectEntryQuery;
	},
	{},
	{},
	{}
>;

export const userSegRedirectEntrypoint: EntryPoint<
	UserSegRedirectEntrypointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<UserSegRedirectEntrypointComponent>(
		/* webpackChunkName: "async-user-seg-redirect-entrypoint" */ './UserSegmentationRedirectEntry.tsx',

		{
			// eslint-disable-next-line jira/entry-fs/restrict-shell-only-to-navigation-apps
			includeInShellOnlySsrBundle: true,
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({ tenantContext }: EntryPointRouteParams) => {
		const shouldSkipQuery = !tenantContext.atlassianAccountId || tenantContext.isAnonymous;

		if (shouldSkipQuery) {
			return {};
		}

		const hasCookie = hasRedirectCheckCookie();
		if (hasCookie) {
			return {};
		}

		return {
			queries: {
				userSegQueryRef: {
					parameters: Query,
					variables: {
						accountId: tenantContext.atlassianAccountId || '',
						cloudId: tenantContext.cloudId,
					},
					options: {
						fetchPolicy,
					},
				},
			},
		};
	},
});
